import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "./i18n";
import { CATEGORIES, MATERIALS, NOT_ACCEPTED, CatId } from "./data/materials";
import { track } from "./analytics";
import "./MaterialDirectory.css";

/**
 * Searchable "What We Take" directory — type an item or tap a category.
 * Data lives in src/data/materials.ts (see the edit guide at the top of that file).
 */

const COPY = {
  en: {
    label: "What We Take",
    title: "Type it. We'll tell you. 🔎",
    sub: "Search anything — from “laptop” to “couch” — or browse by category.",
    placeholder: "Try “iPhone”, “sofa”, “cables”…",
    all: "All",
    hits: (n: number) => `${n} item${n === 1 ? "" : "s"} we take`,
    noRes1: "Hmm, that's not on our list — but we might still take it. ",
    noResStrong: "Ask EcoBot",
    noRes2: " (bottom right) or add a photo when you book and we'll confirm.",
    notLabel: "We can't take:",
    cta: "📦 Schedule a Free Pickup",
  },
  es: {
    label: "Qué Aceptamos",
    title: "Escríbelo. Te decimos. 🔎",
    sub: "Busca lo que sea — de “laptop” a “sofá” — o explora por categoría.",
    placeholder: "Prueba “iPhone”, “sofá”, “cables”…",
    all: "Todo",
    hits: (n: number) => `${n} artículo${n === 1 ? "" : "s"} que aceptamos`,
    noRes1: "Mmm, eso no está en la lista — pero puede que igual lo llevemos. ",
    noResStrong: "Pregúntale a EcoBot",
    noRes2: " (abajo a la derecha) o agrega una foto al agendar y te confirmamos.",
    notLabel: "No podemos llevar:",
    cta: "📦 Agenda tu Recogida Gratis",
  },
};

const MaterialDirectory: React.FC = () => {
  const { lang } = useLang();
  const L = COPY[lang];
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState<CatId | "all">("all");

  // /pickup?cat=tools#accept → open with that category selected
  useEffect(() => {
    try {
      const wanted = new URLSearchParams(window.location.search).get("cat");
      if (wanted && CATEGORIES.some((c) => c.id === wanted)) setCat(wanted as CatId);
    } catch { /* ignore */ }
  }, []);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return MATERIALS.filter((m) => {
      if (cat !== "all" && m.cat !== cat) return false;
      if (!q) return true;
      return (
        m.name.en.toLowerCase().includes(q) ||
        m.name.es.toLowerCase().includes(q) ||
        m.keywords.some((k) => k.includes(q) || q.includes(k))
      );
    });
  }, [query, cat]);

  const onSearch = (value: string) => {
    setQuery(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (value.trim().length > 2) {
      searchTimer.current = setTimeout(() => track("item_searched", { query: value.trim().toLowerCase() }), 600);
    }
  };

  const catIcon = (id: CatId) => CATEGORIES.find((c) => c.id === id)?.icon || "♻️";

  return (
    <section id="accept" className="ee-section md-section">
      <div className="ee-container">
        <div className="ee-section-header" style={{ textAlign: "center", maxWidth: 640, margin: "0 auto 40px" }}>
          <div className="ee-section-label" style={{ justifyContent: "center" }}>{L.label}</div>
          <h2 className="ee-section-title">{L.title}</h2>
          <p className="ee-section-sub" style={{ margin: "0 auto" }}>{L.sub}</p>
        </div>

        <div className="md-search-wrap">
          <span className="md-search-ico" aria-hidden="true">🔎</span>
          <input
            type="search"
            className="md-search"
            placeholder={L.placeholder}
            value={query}
            onChange={(e) => onSearch(e.target.value)}
            aria-label={L.placeholder}
          />
        </div>

        <div className="md-cats" role="group" aria-label={L.label}>
          <button
            className={`md-cat${cat === "all" ? " on" : ""}`}
            onClick={() => setCat("all")}
          >
            ♻️ {L.all}
          </button>
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              className={`md-cat${cat === c.id ? " on" : ""}`}
              onClick={() => {
                setCat(cat === c.id ? "all" : c.id);
                track("item_category_selected", { category: c.id });
              }}
            >
              {c.icon} {c.title[lang]}
            </button>
          ))}
        </div>

        {results.length > 0 ? (
          <>
            <p className="md-count" aria-live="polite">✅ {L.hits(results.length)}</p>
            <div className="md-grid">
              {results.map((m) => (
                <div className="md-item" key={m.name.en}>
                  <span className="md-item-ico" aria-hidden="true">{catIcon(m.cat)}</span>
                  <span className="md-item-name">{m.name[lang]}</span>
                  <span className="md-item-check" aria-hidden="true">✓</span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="md-empty" aria-live="polite">
            {L.noRes1}<strong>{L.noResStrong}</strong>{L.noRes2}
          </div>
        )}

        <div className="md-not">
          <span className="md-not-label">🚫 {L.notLabel}</span>
          {NOT_ACCEPTED[lang].map((n) => (
            <span className="md-not-chip" key={n}>{n}</span>
          ))}
        </div>

        <div style={{ textAlign: "center", marginTop: 34 }}>
          <Link to="/pickup" className="ee-btn ee-btn-green" onClick={() => track("pickup_cta_clicked", { source: "material_directory" })}>
            {L.cta}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default MaterialDirectory;
